import { Suspense, useState, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Environment } from '@react-three/drei'
import * as THREE from 'three'

const RoundedVerticalEdgesBox = ({ 
  color, 
  roughness, 
  metalness, 
  width = 1, 
  height = 0.1, 
  depth = 1, 
  radius = 0.1 
}) => {
  const geometry = useMemo(() => {
    // Crear forma rectangular perfecta en planta
    const shape = new THREE.Shape()
    shape.moveTo(-width/2, -depth/2)
    shape.lineTo(width/2, -depth/2)
    shape.lineTo(width/2, depth/2)
    shape.lineTo(-width/2, depth/2)
    shape.closePath()

    // Configurar extrusión con bevel solo en los bordes verticales
    const extrudeSettings = {
      depth: height,
      bevelEnabled: true,
      bevelSize: radius,
      bevelThickness: radius,
      bevelSegments: 8,
      bevelOffset: 0
    }

    const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings)
    geom.rotateX(Math.PI / 2) // Rotar para orientación correcta
    geom.translate(0, height/2, 0) // Centrar en Y
    
    return geom
  }, [width, height, depth, radius])

  return (
    <mesh castShadow geometry={geometry}>
      <meshStandardMaterial
        color={color}
        roughness={roughness}
        metalness={metalness}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

const NoiseMaterial = ({ color, roughness, metalness }) => {
  const noiseTexture = useMemo(() => {
    const size = 512
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const context = canvas.getContext('2d')
    
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        const value = Math.sin(x * 0.05) * Math.cos(y * 0.05) * 0.5 + 0.5
        const grain = Math.random() * 0.2
        const finalValue = Math.min(1, Math.max(0, value + grain)) * 255
        context.fillStyle = `rgb(${finalValue}, ${finalValue}, ${finalValue})`
        context.fillRect(x, y, 1, 1)
      }
    }
    
    const texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.repeat.set(2, 2)
    return texture
  }, [])

  return (
    <meshStandardMaterial
      color={color}
      roughness={roughness}
      metalness={metalness}
      map={noiseTexture}
      roughnessMap={noiseTexture}
    />
  )
}

const PBRDemo = () => {
  const [controls, setControls] = useState({
    color: '#a0a0ff',
    roughness: 0.5,
    metalness: 0.5,
    envIntensity: 1.5,
    edgeRadius: 0.1,
    thickness: 0.1
  })

  return (
    <div style={{
      display: 'flex',
      width: '100vw',
      height: '100vh',
      overflow: 'hidden'
    }}>
      {/* Panel de controles completo */}
      <div style={{
        width: '320px',
        background: 'rgba(30, 30, 30, 0.8)',
        padding: '20px',
        color: 'white',
        boxShadow: '2px 0 15px rgba(0,0,0,0.4)',
        overflowY: 'auto',
        backdropFilter: 'blur(10px)',
        borderRight: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h2 style={{ 
          borderBottom: '1px solid rgba(255,255,255,0.2)', 
          paddingBottom: '12px',
          marginBottom: '20px',
          color: '#aaa'
        }}>
          CONTROLES PBR
        </h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9em' }}>Color base:</label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="color"
              value={controls.color}
              onChange={(e) => setControls({...controls, color: e.target.value})}
              style={{ 
                width: '50px', 
                height: '30px',
                marginRight: '10px',
                cursor: 'pointer'
              }}
            />
            <span>{controls.color.toUpperCase()}</span>
          </div>
        </div>
        
        {['roughness', 'metalness', 'edgeRadius', 'thickness', 'envIntensity'].map((prop) => (
          <div key={prop} style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9em' }}>
              {{
                roughness: `Rugosidad: ${controls.roughness.toFixed(2)}`,
                metalness: `Metalicidad: ${controls.metalness.toFixed(2)}`,
                edgeRadius: `Radio bordes: ${controls.edgeRadius.toFixed(2)}`,
                thickness: `Grosor: ${controls.thickness.toFixed(2)}`,
                envIntensity: `Reflejos: ${controls.envIntensity.toFixed(1)}`
              }[prop]}
            </label>
            <input
              type="range"
              min={0}
              max={prop === 'thickness' ? 0.5 : 
                   prop === 'edgeRadius' ? 0.5 : 
                   prop === 'envIntensity' ? 3 : 1}
              step={prop === 'envIntensity' ? 0.1 : 0.01}
              value={controls[prop]}
              onChange={(e) => setControls({...controls, [prop]: parseFloat(e.target.value)})}
              style={{ 
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                background: 'linear-gradient(90deg, #333, #555)',
                outline: 'none'
              }}
            />
          </div>
        ))}
      </div>

      {/* Escena 3D */}
      <div style={{ flex: 1 }}>
        <Canvas
          shadows
          camera={{ position: [0, 1.5, 4], fov: 45 }}
          gl={{
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: controls.envIntensity,
            antialias: true
          }}
        >
          <Suspense fallback={null}>
            {/* Iluminación */}
            <ambientLight intensity={0.4} />
            <directionalLight
              position={[3, 5, 2]}
              intensity={2.5}
              castShadow
              shadow-mapSize={[2048, 2048]}
              shadow-camera-far={15}
              shadow-bias={-0.0001}
            />
            <directionalLight
              position={[-3, 2, -1]}
              intensity={0.8}
              color="#ffaa88"
            />
            
            {/* Esfera con textura de ruido */}
            <mesh castShadow position={[-1.5, 1, 0]}>
              <sphereGeometry args={[1, 64, 64]} />
              <NoiseMaterial 
                color={controls.color}
                roughness={controls.roughness}
                metalness={controls.metalness}
              />
            </mesh>
            
            {/* Caja con bordes verticales redondeados */}
            <RoundedVerticalEdgesBox
              position={[1.5, controls.thickness/2, 0]}
              color={controls.color}
              roughness={controls.roughness}
              metalness={controls.metalness}
              width={1.5}
              height={controls.thickness}
              depth={1.5}
              radius={controls.edgeRadius}
            />
            
            {/* Suelo */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
              <planeGeometry args={[20, 20]} />
              <shadowMaterial opacity={0.25} />
            </mesh>
            
            <Environment preset="studio" background />
          </Suspense>
          
          <OrbitControls
            minDistance={1}
            maxDistance={10}
            enablePan={true}
            enableZoom={true}
            dampingFactor={0.1}
          />
        </Canvas>
      </div>
    </div>
  )
}

export default PBRDemo